import { useEffect, useState } from 'react';
import logError from 'src/data/services/logError';
import { useEventStore } from 'src/domain/stores/useEventStore';
import type { ApiPage } from 'src/core/models/api/ApiPage';

interface PageRouteParams {
	/** Public identifier from the URL. Case-insensitive, like every other event code in this app. */
	code?: string;
	eventUuid?: string;
	pageId?: string | number;
	data?: ApiPage;
}

interface PageFromRouteOptions {
	/**
	 * The kind of page this screen shows. A list screen's URL names no page --
	 * `/evenement/<code>/nieuws` is "the news of this event" -- so without this the
	 * screen would render its empty state on a shared link.
	 */
	type?: ApiPage['type'];
}

interface PageFromRoute<T extends ApiPage = ApiPage> {
	page: T | null;
	/** True only while a fetch is in flight, so a screen can show a loader instead of an empty state. */
	isLoading: boolean;
}

/**
 * The page a screen should render, from whichever source has it.
 *
 * Screens used to read `route.params.data` and fall back to the copy in the store. That works while
 * you navigate inside the app, because the previous screen hands the whole page object along. It
 * does not work on web from a URL: a cold load has no params beyond what fits in the address bar,
 * and `data` is a complete CMS object. Those screens rendered empty, which is how a shared link
 * ended up promising something it could not deliver.
 *
 * So the order is: the object we were handed, then the store (fresher, and already there after the
 * event loads), then the API by id. The last step is what makes a deep link work at all.
 */
export function usePageFromRoute<T extends ApiPage = ApiPage>(
	params: PageRouteParams | undefined,
	options?: PageFromRouteOptions,
): PageFromRoute<T> {
	const handedOver = params?.data as T | undefined;
	// In-app navigation hands over the whole page and no id (see `navigatePage`), so without this the
	// store lookup below would never run there and a screen would keep rendering the copy it was
	// handed. Pull-to-refresh rewrites the store, not that copy, and would look like it did nothing.
	const pageId = params?.pageId ?? handedOver?.id;

	// A URL carries the event code, because that is what a person can read and forward; the app itself
	// navigates with the uuid. The two meet here: an added event is already in the store, so the code
	// resolves without a round trip. Case-insensitive for the same reason the deeplink handler is --
	// the backend matches codes case-insensitively.
	const resolvedFromCode = useEventStore((state) => {
		if (params?.eventUuid !== undefined || params?.code === undefined) {
			return undefined;
		}

		const needle = params.code.toLowerCase();

		// Matches on uuid too, the same way the deeplink handler does: a link built before the
		// event list loaded carries the uuid in that segment instead of the code.
		return state.eventData.find((event) => event.code.toLowerCase() === needle || event.uuid.toLowerCase() === needle)
			?.uuid;
	});

	const eventUuid = params?.eventUuid ?? resolvedFromCode;

	const expectedType = options?.type;
	const fromStore = useEventStore((state) => {
		if (pageId !== undefined) {
			return state.activeEventView.find((page) => String(page.id) === String(pageId)) as T | undefined;
		}

		// No id to go on, so fall back to the kind of page this screen shows. An event has one
		// news page, one locations page and so on, so this is unambiguous in practice -- and a
		// list screen with no page at all would render "nothing here" on a link that does work.
		if (expectedType === undefined) return undefined;

		const top = state.activeEventView.find((page) => page.type === expectedType);
		if (top) return top as T;

		// One level deeper as well: a programme day (`Program`) hangs under the event's
		// `ProgramHolder`, so `/evenement/<code>/programma` would otherwise find nothing. The
		// first day is the right landing point -- the screen's own day tabs take it from there.
		return state.activeEventView.flatMap((page) => page.children ?? []).find((child) => child.type === expectedType) as
			T | undefined;
	});

	const [fetched, setFetched] = useState<T | null>(null);
	const [isFetchingPage, setIsFetchingPage] = useState(false);

	// A list URL (`/evenement/<code>/nieuws`) names no page, so there is nothing to fetch by id --
	// but the event's own pages are still on their way in. Without this the screen renders its
	// "nothing here" state first and flips to content a moment later, which is exactly what a
	// shared link should not do.
	const isLoadingEvent = useEventStore((state) => state.isLoading);

	const page = fromStore ?? handedOver ?? fetched ?? null;
	const needsFetch = page === null && eventUuid !== undefined && pageId !== undefined;

	useEffect(() => {
		if (!needsFetch) {
			return;
		}

		let cancelled = false;
		setIsFetchingPage(true);

		useEventStore
			.getState()
			.getPage({ eventUuid, pageId })
			.then((data) => {
				if (!cancelled) {
					setFetched(data as T);
				}
			})
			.catch(logError)
			.finally(() => {
				// Deliberately NOT behind the `cancelled` check that guards the result above.
				// Storing the page flips `needsFetch`, which re-runs this effect and therefore
				// runs the cleanup -- so a cancelled-guarded reset here could be skipped for the
				// very run that just succeeded, and the screen would keep its loader over a page
				// it already has.
				setIsFetchingPage(false);
			});

		return () => {
			cancelled = true;
		};
	}, [needsFetch, eventUuid, pageId]);

	// Derived rather than stored: the loader belongs on screen from the first render until there
	// is something to show, and a stored flag starts at false for exactly one frame.
	return { page, isLoading: page === null && (needsFetch || isFetchingPage || isLoadingEvent) };
}

export default usePageFromRoute;
